import {
  buildRealtimeWsUrl,
  getAccessToken,
  isRealtimeSecureAuthEnabled,
  REALTIME_RECONNECT_BASE_MS,
  REALTIME_PROJECTION_DEDUP_TTL_MS
} from './realtimeConfig';

export type RealtimeProjectionKind = 'workspace' | 'dashboard' | 'bell' | 'route_detail' | 'telemetry';

export type RealtimeProjectionMessage = {
  type: 'projection';
  channel: string;
  projection: {
    kind: RealtimeProjectionKind;
    channel: string;
    payload: {
      invalidateTags?: string[];
      routeId?: number;
      timelineEntry?: Record<string, unknown>;
      eventType?: string;
    };
    serverTime: string;
  };
  event?: { eventId?: string; mutationId?: string; eventType?: string };
};

export type CollabMessage = {
  type: 'collab';
  channel: string;
  kind: string;
  payload: Record<string, unknown>;
};

type ProjectionListener = (msg: RealtimeProjectionMessage) => void;
type CollabListener = (msg: CollabMessage) => void;

class OperationalRealtimeClient {
  private socket: WebSocket | null = null;
  private listeners = new Set<ProjectionListener>();
  private collabListeners = new Set<CollabListener>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private userId: number | null = null;
  private connected = false;
  private authenticated = false;
  private routeIds = new Set<number>();
  private baseChannels: string[] = [];
  private seenProjectionKeys = new Map<string, number>();

  connect(userId: number, channels: string[]): void {
    this.userId = userId;
    this.baseChannels = channels;
    const token = getAccessToken();
    if (!token) return;

    this.disconnectSocketOnly();
    this.socket = new WebSocket(buildRealtimeWsUrl());

    this.socket.onopen = () => {
      this.connected = true;
      this.reconnectAttempt = 0;
      if (isRealtimeSecureAuthEnabled()) {
        this.send({ action: 'auth', token });
      } else {
        this.authenticated = true;
        this.subscribeAll();
      }
    };

    this.socket.onmessage = (ev) => {
      try {
        const msg = JSON.parse(String(ev.data));
        this.handleServerMessage(msg);
      } catch {
        /* ignore malformed */
      }
    };

    this.socket.onclose = () => {
      this.connected = false;
      this.authenticated = false;
      this.disconnectSocketOnly();
      this.scheduleReconnect();
    };

    this.socket.onerror = () => {
      this.socket?.close();
    };
  }

  subscribeRoute(routeId: number): void {
    if (!Number.isFinite(routeId)) return;
    this.routeIds.add(routeId);
    if (this.authenticated) {
      this.send({ action: 'subscribe', channels: [`route:${routeId}`] });
    }
  }

  unsubscribeRoute(routeId: number): void {
    this.routeIds.delete(routeId);
    if (this.authenticated) {
      this.send({ action: 'unsubscribe', channels: [`route:${routeId}`] });
    }
  }

  collabRouteSessionStart(routeId: number, mode: 'edit' | 'view'): void {
    this.send({ action: 'collab_route_session_start', routeId, mode });
  }

  collabRouteSessionPing(routeId: number): void {
    this.send({ action: 'collab_route_session_ping', routeId });
  }

  collabRouteSessionEnd(routeId: number): void {
    this.send({ action: 'collab_route_session_end', routeId });
  }

  collabWorkspacePing(activity?: string): void {
    this.send({ action: 'collab_workspace_ping', activity: activity ?? 'workspace_overview' });
  }

  collabFinancePing(): void {
    this.send({ action: 'collab_finance_ping' });
  }

  requestResync(): void {
    this.send({ action: 'resync' });
  }

  isConnected(): boolean {
    return this.connected && this.authenticated && this.socket?.readyState === WebSocket.OPEN;
  }

  onProjection(listener: ProjectionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onCollab(listener: CollabListener): () => void {
    this.collabListeners.add(listener);
    return () => this.collabListeners.delete(listener);
  }

  /** Full teardown (e.g. logout / layout unmount): clears reconnect and route subscriptions. */
  disconnect(clearReconnect = true, clearRoutes = true): void {
    if (clearReconnect && this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (clearRoutes) this.routeIds.clear();
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
    this.connected = false;
    this.authenticated = false;
  }

  private disconnectSocketOnly(): void {
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      s.onclose = null;
      s.close();
    }
    this.connected = false;
    this.authenticated = false;
  }

  private handleServerMessage(msg: Record<string, unknown>): void {
    const type = String(msg.type || '');

    if (type === 'auth_required') return;
    if (type === 'auth_ok') {
      this.authenticated = true;
      this.subscribeAll();
      return;
    }
    if (type === 'auth_failed' || type === 'auth_expired') {
      this.authenticated = false;
      this.socket?.close();
      return;
    }

    if (type === 'collab') {
      const envelope: CollabMessage = {
        type: 'collab',
        channel: String(msg.channel || ''),
        kind: String(msg.kind || ''),
        payload: typeof msg.payload === 'object' && msg.payload !== null ? (msg.payload as Record<string, unknown>) : {}
      };
      this.collabListeners.forEach((fn) => fn(envelope));
      return;
    }

    if (type === 'projection') {
      const dedupKey = `${msg.channel}:${(msg.event as { eventId?: string })?.eventId ?? ''}:${(msg.projection as { serverTime?: string })?.serverTime ?? ''}`;
      const now = Date.now();
      const seenAt = this.seenProjectionKeys.get(dedupKey);
      if (seenAt && now - seenAt < REALTIME_PROJECTION_DEDUP_TTL_MS) return;
      this.seenProjectionKeys.set(dedupKey, now);
      this.pruneDedup(now);

      this.listeners.forEach((fn) => fn(msg as RealtimeProjectionMessage));
    }
  }

  private pruneDedup(now: number): void {
    if (this.seenProjectionKeys.size < 500) return;
    for (const [k, ts] of Array.from(this.seenProjectionKeys.entries())) {
      if (now - ts > REALTIME_PROJECTION_DEDUP_TTL_MS) this.seenProjectionKeys.delete(k);
    }
  }

  private subscribeAll(): void {
    const routeChannels = Array.from(this.routeIds).map((id) => `route:${id}`);
    const all = [...this.baseChannels, ...routeChannels];
    this.send({ action: 'subscribe', channels: all });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || !this.userId) return;
    const delay = Math.min(REALTIME_RECONNECT_BASE_MS * 2 ** this.reconnectAttempt, 30_000);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.userId) this.connect(this.userId, this.baseChannels);
    }, delay);
  }

  private send(payload: Record<string, unknown>): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }
}

export const operationalRealtimeClient = new OperationalRealtimeClient();
